import { DebitNoteRepository } from "../../domain/repositories/DebitNoteRepository";
import {
  DebitNoteEntity,
  DebitNoteDataEntity,
  DebitNoteOperationResult,
  DebitNotesResultEntity,
  DebitNoteCountsEntity,
  DebitNoteListStatus,
} from "../../domain/entities/DebitNoteEntity";
import {
  getCreatedInBackendSuccessMessage,
  getFailedCreateInBackendMessage,
  getFailedUpdateAndPostMessage,
  getFailedUpdateInBackendMessage,
  getStoredAccountingBackend,
  getUpdatedAndPostedSuccessMessage,
  getUpdatedInBackendSuccessMessage,
} from "../../../../config/accounting";
import {
  fetchDebitNotes,
  fetchDebitNoteCounts,
  updateDebitNoteJson,
  createZohoDebitNote,
  updateZohoDebitNote,
  rejectDebitNote,
  approveDebitNote,
  getDebitNoteById,
  saveDebitNoteAsDraft,
} from "../api/debitNotesApi";
import { DebitNoteMapper } from "../mappers/DebitNoteMapper";

export class DebitNoteRepositoryImpl implements DebitNoteRepository {
  /**
   * Fetch debit notes through the unified list endpoint.
   */
  async fetchDebitNotes(params?: {
    limit?: number;
    offset?: number;
    status?: DebitNoteListStatus;
    search?: string;
    date_from?: string;
    date_to?: string;
    sort_by?: "created_at" | "updated_at" | "document_number" | "status";
    sort_order?: "asc" | "desc";
  }): Promise<DebitNotesResultEntity> {
    const response = await fetchDebitNotes(params);
    return DebitNoteMapper.toDebitNotesResultEntity(response);
  }

  /**
   * Get a single debit note by ID
   */
  async getDebitNoteById(debitNoteId: number): Promise<DebitNoteEntity> {
    try {
      const response = await getDebitNoteById(debitNoteId);
      if (response.success && response.data) {
        return DebitNoteMapper.toDebitNoteEntity(response.data);
      } else {
        throw new Error(response.message || "Failed to fetch debit note");
      }
    } catch (error: any) {
      throw new Error(error.message || "Failed to fetch debit note");
    }
  }

  /**
   * Update debit note JSON data (for auto-save and manual edits)
   */
  async updateDebitNoteJson(
    debitNoteId: number,
    debitNoteData: DebitNoteDataEntity
  ): Promise<DebitNoteOperationResult> {
    try {
      // Call API
      await updateDebitNoteJson(debitNoteId, debitNoteData);

      return {
        success: true,
        message: "Debit note updated successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to update debit note",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Create debit note in Zoho Books
   * Creates a new debit note in the database and pushes it to Zoho Books
   * If debitNoteId is provided, updates the existing debit note instead
   */
  async createZohoDebitNote(
    debitNoteData: DebitNoteDataEntity,
    pdfUrl?: string,
    debitNoteId?: number
  ): Promise<DebitNoteOperationResult> {
    try {
      const backend = getStoredAccountingBackend();
      // Call API with optional debitNoteId for updating existing drafts
      await createZohoDebitNote(debitNoteData, pdfUrl, debitNoteId);

      return {
        success: true,
        message: debitNoteId
          ? getUpdatedAndPostedSuccessMessage("Debit note", backend)
          : getCreatedInBackendSuccessMessage("Debit note", backend),
      };
    } catch (error: any) {
      const backend = getStoredAccountingBackend();
      return {
        success: false,
        message: debitNoteId
          ? getFailedUpdateAndPostMessage("debit note", backend)
          : getFailedCreateInBackendMessage("debit note", backend),
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Update debit note in Zoho Books
   */
  async updateZohoDebitNote(
    debitNoteId: number,
    debitNoteData: DebitNoteDataEntity
  ): Promise<DebitNoteOperationResult> {
    try {
      const backend = getStoredAccountingBackend();
      // Call API
      await updateZohoDebitNote(debitNoteId, debitNoteData);

      return {
        success: true,
        message: getUpdatedInBackendSuccessMessage("Debit note", backend),
      };
    } catch (error: any) {
      const backend = getStoredAccountingBackend();
      return {
        success: false,
        message: getFailedUpdateInBackendMessage("debit note", backend),
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Reject a debit note
   */
  async rejectDebitNote(
    debitNoteId: number
  ): Promise<DebitNoteOperationResult> {
    try {
      // Call API
      await rejectDebitNote(debitNoteId);

      return {
        success: true,
        message: "Debit note rejected successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to reject debit note",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Approve a debit note
   */
  async approveDebitNote(
    debitNoteId: number
  ): Promise<DebitNoteOperationResult> {
    try {
      // Call API
      await approveDebitNote(debitNoteId);

      return {
        success: true,
        message: "Debit note approved successfully",
      };
    } catch (error: any) {
      return {
        success: false,
        message: "Failed to approve debit note",
        error: error.message || "Unknown error",
      };
    }
  }

  /**
   * Fetch debit note counts for dashboard cards
   */
  async fetchDebitNoteCounts(params?: {
    date_from?: string;
    date_to?: string;
  }): Promise<DebitNoteCountsEntity> {
    const response = await fetchDebitNoteCounts(params);
    return DebitNoteMapper.toDebitNoteCountsEntity(response);
  }

  /**
   * Save debit note as draft
   * Creates a new debit note with draft status (0)
   */
  async saveDebitNoteAsDraft(
    debitNoteData: DebitNoteDataEntity,
    options?: {
      jobid?: string | null;
      folder_id?: string | null;
      company_id?: number | null;
      gst_rates?: any;
      gst_types?: any;
      is_tds_applied?: number;
      tds_tax_id?: string | null;
      tds_tax_amount?: number | null;
      tds_tax_percentage?: number | null;
      tds_tax_name?: string | null;
      tds_section?: string | null;
    }
  ): Promise<DebitNoteEntity> {
    try {
      const response = await saveDebitNoteAsDraft(debitNoteData, options);

      if (response.success && response.data?.debit_note) {
        // Map the response to DebitNoteEntity
        const debitNote = response.data.debit_note;
        return DebitNoteMapper.toDebitNoteEntity(debitNote);
      } else {
        throw new Error(
          response.message || "Failed to save debit note as draft"
        );
      }
    } catch (error: any) {
      throw new Error(error.message || "Failed to save debit note as draft");
    }
  }
}
